import { EventData, FormMetadata, SubmissionData, Rating, VocApiPayload } from './Types'
import { handleRequest, METHOD } from '../../utils/handleRequest'
import { FeedbackModalSubmissionMetricsPublisher } from '../../utils/metrics'
import { logger } from '../../utils/logger'
import { logWithTakt, getTakt } from '../../utils/takt'
import { getDebugConfig } from './debugger'
import removeNullAndUndefinedValue from './removeNullAndUndefinedValue'

const eventType = 'onclick'

type QuestionAnswerPairs = Record<string, unknown>
type AnswerSet = Record<string, unknown[]>

const getEventId = (eventDetail: EventData) => `${eventDetail.app}-${eventDetail.page}-${eventDetail.component}${eventDetail.instanceId ? `-${eventDetail.instanceId}` : ''}`

const addAnswerToAnswerset = ({
  type,
  questionValue,
  answerValue,
  answerSets,
}: {
  type: string
  questionValue: string
  answerValue: string
  answerSets: { fixedAnswerSet?: AnswerSet; freeformAnswerSet?: AnswerSet }
}) => {
  if (['rating', 'dropdown'].includes(type)) {
    if (!answerSets.fixedAnswerSet) {
      answerSets.fixedAnswerSet = {}
    }
    answerSets.fixedAnswerSet[questionValue] = [answerValue]
  } else {
    if (!answerSets.freeformAnswerSet) {
      answerSets.freeformAnswerSet = {}
    }
    answerSets.freeformAnswerSet[questionValue] = [answerValue]
  }
}

const getRatingScaleDescription = (rating: Rating) => {
  const { ratingDescription, ratingType } = rating
  let min = 1
  let max = 5

  if (ratingDescription) {
    const values = Object.keys(ratingDescription)
      .map((v) => parseInt(v, 10))
      .sort((a, b) => a - b)
    min = values[0]
    max = values[values.length - 1]
  } else if (ratingType === 'NPS_10') {
    min = 0
    max = 10
  }

  return `on a scale from ${min}-${max}`
}

export const generateAnswerSets = (
  values: Record<string, unknown>,
  feedbackModalData: FormMetadata
): {
  questionAnswerPairs: QuestionAnswerPairs
  answerSets: { fixedAnswerSet?: AnswerSet; freeformAnswerSet?: AnswerSet }
} =>
  Object.entries(values).reduce(
    (result, [question, answer]) => {
      if (answer) {
        let questionValue = ''
        let answerValue = answer as string
        switch (true) {
          case question === 'rating': {
            const rating = feedbackModalData[question]
            if (rating) {
              const questionInEnglish = rating.ratingTitleInEnglish
              questionValue = `${questionInEnglish} (${getRatingScaleDescription(rating)})`
              answerValue = answer as string
            }
            break
          }
          case question === 'dropdown': {
            const dropdown = feedbackModalData[question]
            if (dropdown) {
              const questionInEnglish = dropdown.dropdownTitleInEnglish
              const answerInEnglish = dropdown.dropdownItems.find(({ itemValue }) => itemValue === answer)?.itemValueInEnglish ?? (answer as string)
              questionValue = questionInEnglish
              answerValue = answerInEnglish
            }
            break
          }
          case question === 'inputTextArea': {
            const inputTextArea = feedbackModalData[question]
            if (inputTextArea) {
              const questionInEnglish = inputTextArea.labelInEnglish
              questionValue = questionInEnglish
              answerValue = answer as string
            }
            break
          }
        }

        result.questionAnswerPairs[questionValue] = answerValue
        addAnswerToAnswerset({
          type: question,
          answerSets: result.answerSets,
          questionValue,
          answerValue,
        })
      }
      return result
    },
    { questionAnswerPairs: {}, answerSets: { fixedAnswerSet: {}, freeformAnswerSet: {} } }
  )

// VOC API https://console.harmony.a2z.com/swagger-ui/?id=MTc2NjE4MjM4MjgxOnZvY2ZlZWRiYWNrbWFuYWdlbWVudHNlcnZpY2U6dXMtZWFzdC0x#/feedback/post_v0_feedback
export const handleSubmit = async (
  values: Record<string, unknown>,
  eventDetail: EventData,
  feedbackModalData: FormMetadata,
  submissionData: SubmissionData | undefined,
  locale: string,
  vocApiPayload: VocApiPayload
): Promise<void | { error: string }> => {
  const { simulateVocApiCall } = getDebugConfig({ application: eventDetail.app, page: eventDetail.page, component: eventDetail.component }) ?? {}

  if (simulateVocApiCall === 'success') {
    return undefined
  }

  if (simulateVocApiCall === 'failure') {
    throw new Error('simulateVocApiCall is failure')
  }

  if (!submissionData) {
    throw new Error('Ajax API response not available')
  }

  const { questionAnswerPairs, answerSets } = generateAnswerSets(values, feedbackModalData)
  const submissionTaktEventData = logSubmitTakt(questionAnswerPairs, eventDetail)
  const logMessage = `asyncRequests - submit feedback form data for app=${eventDetail?.app}&page=${eventDetail?.page}&component=${eventDetail?.component}${
    eventDetail?.instanceId ? `&instanceId=${eventDetail?.instanceId}` : ''
  }`
  const submissionResult = await handleRequest(
    {
      method: METHOD.POST,
      url: submissionData.submissionApiEndpoint,
      withCredentials: false,
      headers: {
        Authorization: submissionData.submissionApiIdToken,
        'Content-Type': 'application/json',
      },
      data: constructRequestBody(answerSets, eventDetail, feedbackModalData, submissionData, locale, vocApiPayload),
    },
    logMessage,
    FeedbackModalSubmissionMetricsPublisher
  )

  if (!submissionResult) {
    FeedbackModalSubmissionMetricsPublisher.publishCounterMonitor(`failure-${getEventId(eventDetail)}`, 1)
    throw new Error('No response VOC feedback submission API')
  }

  if (submissionResult.status !== 200) {
    FeedbackModalSubmissionMetricsPublisher.publishCounterMonitor(`error-${submissionResult.status}-${getEventId(eventDetail)}`, 1)
    let message = `Invalid response from VOC feedback submission API, response code: ${submissionResult.status}`
    if (submissionResult.data) {
      message += `, response body: ${JSON.stringify(submissionResult.data)}`
    }
    logger.info(message)
    throw new Error(message)
  }

  FeedbackModalSubmissionMetricsPublisher.publishCounterMonitor(`success-${getEventId(eventDetail)}`, 1)
  logWithTakt(`cssf-feedback-submit-success-${getEventId(eventDetail)}`, {
    ...submissionTaktEventData,
    feedbackId: submissionResult.data.feedbackId,
  })

  return undefined
}

const constructRequestBody = (
  answerSet: { fixedAnswerSet?: AnswerSet; freeformAnswerSet?: AnswerSet },
  eventDetail: EventData,
  feedbackModalData: FormMetadata,
  submissionData: SubmissionData,
  locale: string,
  vocApiPayload: VocApiPayload
) => {
  const timestamp = new Date().getTime()
  const takt = getTakt()
  const submissionInfo = {
    createDate: timestamp,
    lastUpdatedDate: timestamp,
    ingressType: feedbackModalData.ingressType,
    surveyId: feedbackModalData.surveyId,
    surveyName: feedbackModalData.surveyName,
    pageUrl: document.location.href,
    fixedAnswerSet: answerSet.fixedAnswerSet,
    freeformAnswerSet: answerSet.freeformAnswerSet,
    ...(takt
      ? {
          taktSessionId: takt['session_id'],
          taktVisitId: takt['visit_id'],
        }
      : {}),
    ...removeNullAndUndefinedValue(vocApiPayload.submissionInfo),
  }
  const advertiserInfo = {
    primaryAccountId: submissionData.primaryAccountId,
    primaryAccountIdType: submissionData.primaryAccountType,
    languagePref: locale,
    accountType: submissionData.accountType,
    customerId: submissionData.customerId,
    // Adding default value as empty string will result in VOC API error
    advertisingArea: 'unknown',
    // Get the accountName from takt if entityType matched
    ...(takt?.['user_metadata']?.entityType === submissionData.accountType && takt?.['user_metadata']?.entityName
      ? {
          accountName: takt?.['user_metadata'].entityName,
        }
      : {}),
    ...removeNullAndUndefinedValue(vocApiPayload.advertiserInfo),
  }
  const DELIMITER = ':'
  const feedbackContext = {
    pageId: eventDetail.page,
    appId: eventDetail.app,
    componentId: `${eventDetail.component}${eventDetail.instanceId ? `${DELIMITER}${eventDetail.instanceId}` : ''}`,
    eventType: eventType,
    marketplaceId: submissionData.marketplaceId,
    feedbackCategory: feedbackModalData.applicationContext?.advertisingArea || 'unknown',
    ...removeNullAndUndefinedValue(vocApiPayload.feedbackContext),
  }

  return {
    submissionInfo,
    advertiserInfo,
    feedbackContext,
  }
}

const logSubmitTakt = (questionAnswerPairs: QuestionAnswerPairs, eventDetail: EventData) => {
  const taktId = `cssf-feedback-submit-${getEventId(eventDetail)}`
  const taktEventData = {
    ...questionAnswerPairs,
    feedbackApp: eventDetail.app,
    feedbackPageId: eventDetail.page,
    feedbackComponent: eventDetail.component,
    ...(eventDetail.instanceId ? { feedbackInstanceId: eventDetail.instanceId } : {}),
    pageUrl: document.URL,
  }
  logWithTakt(taktId, taktEventData)
  return taktEventData
}
